import { apiHelpers, extractApiData, parseApiResponse } from '@/api/core';
import { useMutation } from '@tanstack/react-query';
import type {
  SendVerificationLinkRequest,
  SendVerificationLinkResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  RegistrationStatusRequest,
  RegistrationStatusResponse,
} from '@packages/types/auth';

// 이메일 인증
export const useVerifyEmail = (options?: {
  onSuccess?: (data: VerifyEmailResponse) => void;
  onError?: (error: any) => void;
  onSettled?: () => void;
}) => {
  return useMutation({
    mutationFn: async (data: VerifyEmailRequest): Promise<VerifyEmailResponse> => {
      try {
        const response = await apiHelpers.post<VerifyEmailResponse>('auth.verifyEmail', data);
        const parsed = parseApiResponse<VerifyEmailResponse>(response);
        return parsed.data ?? (response.data as unknown as VerifyEmailResponse);
      } catch (error: unknown) {
        const axiosErr = error as { response?: { data?: unknown } };
        console.error('[useVerifyEmail] Error in mutationFn:', error);
        console.error('[useVerifyEmail] Error response:', axiosErr.response);
        console.error('[useVerifyEmail] Error response.data:', axiosErr.response?.data);
        console.error('[useVerifyEmail] About to throw error...');
        // 에러를 다시 throw하여 onError 콜백이 호출되도록 함
        throw error;
      }
    },
    onSuccess: options?.onSuccess,
    onError: options?.onError,
    onSettled: options?.onSettled,
  });
};

// 이메일 인증 링크 발송
export const sendVerificationLink = async (
  data: SendVerificationLinkRequest
): Promise<SendVerificationLinkResponse> => {
  try {
    const response = await apiHelpers.post<SendVerificationLinkResponse>(
      'auth.sendVerificationLink',
      data
    );

    // 백엔드가 { success, message, code } 형태로 직접 반환하는 경우 처리
    const responseData = response.data as any;

    // 이미 SendVerificationLinkResponse 형태인 경우 그대로 반환
    if (responseData && typeof responseData === 'object' && 'success' in responseData) {
      return responseData as SendVerificationLinkResponse;
    }

    // parseApiResponse로 파싱 시도 (data 필드로 감싸진 경우)
    const parsed = parseApiResponse(response);
    if (parsed.data && typeof parsed.data === 'object' && 'success' in parsed.data) {
      return parsed.data as SendVerificationLinkResponse;
    }

    // response.data가 SendVerificationLinkResponse 형태인 경우
    if (responseData && typeof responseData === 'object') {
      return responseData as SendVerificationLinkResponse;
    }

    // 예상치 못한 응답 형태
    throw new Error('Unexpected response format from sendVerificationLink API');
  } catch (error: unknown) {
    // 네트워크 에러나 기타 에러 처리
    // axios 에러인 경우 response.data 확인
    const axiosErr = error as { response?: { data?: unknown } };
    if (axiosErr.response?.data) {
      const errorData = axiosErr.response.data;
      // 백엔드에서 에러 응답을 보낸 경우 (success: false 포함)
      if (errorData && typeof errorData === 'object' && 'success' in errorData) {
        return errorData as SendVerificationLinkResponse;
      }
    }

    // 네트워크 에러나 기타 에러는 그대로 throw
    throw error;
  }
};

// 이메일 등록 상태 확인
export const getRegistrationStatus = async (
  data: RegistrationStatusRequest
): Promise<RegistrationStatusResponse> => {
  const response = await apiHelpers.get<RegistrationStatusResponse>(
    'auth.getRegistrationStatus',
    undefined,
    { email: data.email }
  );
  return extractApiData(response);
};
